import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  ApolloLink,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import axios from "axios";
import store from "./app/store";
import { PrimeReactProvider } from "primereact/api";
import { Provider } from "react-redux";

// ── Refresh helper ────────────────────────────────────────────────────────────
// Calls refreshOperatorToken and returns true if a new access token was issued.
// The server sets the new mboxtoken cookie automatically in the response.

const REFRESH_MUTATION = `
  query RefreshOperatorToken {
    refreshOperatorToken
  }
`;

let isRefreshing = false;
let pendingRequests = [];

const resolvePending = () => {
  pendingRequests.forEach((resolve) => resolve());
  pendingRequests = [];
};

const rejectPending = () => {
  pendingRequests.forEach((_, i, arr) => arr[i]?.(false));
  pendingRequests = [];
};

async function tryRefreshToken() {
  const response = await fetch("/graphql/", {
    method: "POST",
    credentials: "include", // must send the mboxrefresh cookie
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ query: REFRESH_MUTATION }),
  });

  const json = await response.json();
  return json?.data?.refreshOperatorToken === true;
}

const ACCESS_EXPIRED_MSG = "Access token expired or invalid";
const SESSION_DEAD_MSGS = [
  "Access Denied. Please login again.",
  "Session expired. Please login again.",
];

// Shared helper — components import this to silently skip auth errors
export const isAuthError = (err) => err?.__isAuthError === true;

const fireSessionExpired = () => {
  window.dispatchEvent(new CustomEvent("sessionExpired"));
};

// Returns a tagged error so component catch blocks can detect it
const authError = () =>
  Object.assign(new Error("Session expired. Please log in again."), {
    __isAuthError: true,
  });

// ── Error link ────────────────────────────────────────────────────────────────
// Intercepts "Access token expired" errors, refreshes, then retries once.

const errorLink = onError(({ graphQLErrors, operation, forward }) => {
  if (!graphQLErrors) return;

  // Immediately kick to login — no refresh attempt
  const isSessionDead = graphQLErrors.some((err) =>
    SESSION_DEAD_MSGS.includes(err.message),
  );
  if (isSessionDead) {
    fireSessionExpired();
    return;
  }

  // Only refresh on access token expiry — match loosely so server wording doesn't matter
  const isExpired = graphQLErrors.some((err) =>
    err.message.includes(ACCESS_EXPIRED_MSG),
  );
  if (!isExpired) return;

  // Return an Observable so Apollo waits for the refresh before retrying
  return new Observable((observer) => {
    if (isRefreshing) {
      // Another request is already refreshing — queue this one
      pendingRequests.push(() => {
        forward(operation).subscribe(observer);
      });
      return;
    }

    isRefreshing = true;

    tryRefreshToken()
      .then((success) => {
        if (success) {
          resolvePending();
          // Retry the original operation — cookie is now updated
          forward(operation).subscribe(observer);
        } else {
          // Refresh token is also expired — force re-login
          rejectPending();
          fireSessionExpired();
          observer.error(authError());
        }
      })
      .catch(() => {
        rejectPending();
        fireSessionExpired();
        observer.error(authError());
      })
      .finally(() => {
        isRefreshing = false;
      });
  });
});

// ── HTTP link ─────────────────────────────────────────────────────────────────

const httpLink = new HttpLink({
  uri: process.env.REACT_APP_backendUri,
  credentials: "include",
});

// ── Apollo client ─────────────────────────────────────────────────────────────

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, httpLink]),
  cache: new InMemoryCache(),
});

// ── Axios interceptor — covers REST API auth errors ───────────────────────────

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401 || status === 403) {
      fireSessionExpired();
      return Promise.reject(Object.assign(error, { __isAuthError: true }));
    }
    return Promise.reject(error);
  },
);

const value = {
  appendTo: "self",
};
const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <Provider store={store}>
    <ApolloProvider client={client}>
      <React.StrictMode>
        <PrimeReactProvider value={value}>
          <App />
        </PrimeReactProvider>
      </React.StrictMode>
    </ApolloProvider>
  </Provider>,
);

reportWebVitals();
